import { isTransientProductionFailure } from "../operator-connection";
import type { TerminalBootstrap } from "../models";

export type TerminalFailure = { code: string; message: string };

export type TerminalScreenState =
  | { kind: "loading"; reconnecting: boolean }
  | { kind: "offline" }
  | { kind: "enrollment" }
  | { kind: "unpaired" }
  | { kind: "not-found" }
  | { kind: "setup" }
  | { kind: "unassigned"; context: TerminalBootstrap }
  | { kind: "login"; context: TerminalBootstrap }
  | { kind: "unlock"; context: TerminalBootstrap }
  | { kind: "active"; context: TerminalBootstrap; reconnecting: boolean };

const enrollmentFailureCodes = new Set([
  "TERMINAL_DEVICE_ENROLLMENT_REQUIRED",
  "TERMINAL_DEVICE_VERIFICATION_FAILED",
]);

export function terminalScreenState(input: {
  loading: boolean;
  reconnecting: boolean;
  context: TerminalBootstrap | null;
  failure: TerminalFailure | null;
  terminalCode: string;
}): TerminalScreenState {
  if (input.loading || (input.reconnecting && !input.context)) {
    return { kind: "loading", reconnecting: input.reconnecting };
  }
  if (!input.context) {
    if (isTransientProductionFailure(input.failure?.code ?? null)) {
      return { kind: "offline" };
    }
    if (
      input.terminalCode &&
      input.failure &&
      enrollmentFailureCodes.has(input.failure.code)
    ) {
      return { kind: "enrollment" };
    }
    if (input.failure?.code === "TERMINAL_UNPAIRED") {
      return { kind: "unpaired" };
    }
    if (input.failure?.code === "TERMINAL_NOT_FOUND") {
      return { kind: "not-found" };
    }
    return { kind: "setup" };
  }
  if (!input.context.assignments.length) {
    return { kind: "unassigned", context: input.context };
  }
  if (!input.context.session || input.context.session.status === "CLOSED") {
    return { kind: "login", context: input.context };
  }
  if (input.context.session.status === "LOCKED") {
    return { kind: "unlock", context: input.context };
  }
  return {
    kind: "active",
    context: input.context,
    reconnecting:
      input.reconnecting ||
      isTransientProductionFailure(input.failure?.code ?? null),
  };
}
